import React, { useCallback, useMemo } from 'react'
import { Modal, Spin, Tooltip } from '@surecompanies/core_components'
import { FileOutlined, InfoCircleOutlined } from '@ant-design/icons'
import DocumentDropzone from './DocumentDropzone'
import UploadedCard from './DocumentItemCard'
import styles from './PlanOverridesComponents.module.scss'
import { useOverrideDocuments } from '../hooks/useOverrideDocuments'
import { useUploadOverrideDocument } from '../hooks/useUploadOverrideDocument'
import { useDeleteOverrideDocument } from '../hooks/useDeleteOverrideDocument'
import type { useEditDocumentStaging } from '../hooks/useEditDocumentStaging'

const MAX_FILES = 3

type Staging = ReturnType<typeof useEditDocumentStaging>

interface Props {
  ruleId: string
  readOnly?: boolean
  // When provided, the section operates in staged mode: trash + drop only
  // mutate the staging buffer; persistence happens on `staging.commit()`.
  staging?: Staging
  // Override the rule API base path (e.g. for location overrides).
  ruleBase?: string
}

export default function DocumentSection({
  ruleId,
  readOnly = false,
  staging,
  ruleBase,
}: Props) {
  const { documents, isLoading } = useOverrideDocuments(ruleId, ruleBase)
  const isStaged = !!staging
  const visibleDocCount = useMemo(() => {
    if (!isStaged) return documents.length
    return (
      documents.filter((d) => !staging.stagedDeleteIds.has(d.id)).length +
      staging.stagedUploads.length
    )
  }, [documents, staging?.stagedDeleteIds, staging?.stagedUploads])

  // Immediate-mode hooks (only used when staging is absent)
  const { upload: immediateUpload, placeholders } = useUploadOverrideDocument({
    ruleId,
    currentDocumentCount: documents.length,
    ruleBase,
  })
  const { deleteDocument: immediateDelete, isDeleting } =
    useDeleteOverrideDocument(ruleId, ruleBase)

  const displayedDocuments = useMemo(() => {
    if (isStaged) {
      return documents.filter((d) => !staging.stagedDeleteIds.has(d.id))
    }
    return documents
  }, [documents, isStaged, staging?.stagedDeleteIds])

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      if (isStaged && staging) {
        files.forEach((file) => {
          staging.stageUpload(file, documents.length)
        })
        return
      }
      files.forEach((file) => void immediateUpload(file))
    },
    [isStaged, staging, immediateUpload, documents.length]
  )

  const handleDeleteClick = useCallback(
    (documentId: string) => {
      if (isStaged && staging) {
        // Stage the deletion immediately, no confirm — Save commits, Cancel reverts.
        staging.stageDelete(documentId)
        return
      }
      Modal.confirm({
        title: 'Remove document',
        content:
          'Remove this document from the override? It will still appear in history.',
        okText: 'Remove',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          await immediateDelete(documentId)
        },
      })
    },
    [isStaged, staging, immediateDelete]
  )

  const handleUnstageUpload = useCallback(
    (tempId: string) => {
      if (!staging) return
      staging.unstageUpload(tempId)
    },
    [staging]
  )

  const totalCount = isStaged
    ? visibleDocCount
    : displayedDocuments.length + placeholders.length

  if (isLoading) {
    return <Spin size="small" />
  }

  const allFileItems = (
    <>
      {displayedDocuments.map((doc) => (
        <UploadedCard
          key={doc.id}
          ruleId={ruleId}
          document={doc}
          readOnly={readOnly}
          onDelete={readOnly ? undefined : handleDeleteClick}
          ruleBase={ruleBase}
        />
      ))}
      {isStaged &&
        staging &&
        staging.stagedUploads.map((u) => (
          <div key={u.tempId} className={styles.documentItemCard}>
            <span className={styles.documentItemBadgePlaceholder}>+</span>
            <div className={styles.documentItemInfo}>
              <span className={styles.documentItemName}>{u.fileName}</span>
              <span className={styles.documentItemMeta}>
                Pending upload — will save on Update
              </span>
            </div>
            <button
              type="button"
              onClick={() => handleUnstageUpload(u.tempId)}
              className={`${styles.documentItemActionBtn} ${styles.documentItemActionBtnDanger}`}
              aria-label={`Cancel pending upload ${u.fileName}`}
            >
              ×
            </button>
          </div>
        ))}
      {!isStaged &&
        placeholders.map((p) => (
          <div key={p.id} className={styles.documentItemCard}>
            <span className={styles.documentItemBadgePlaceholder}>…</span>
            <div className={styles.documentItemInfo}>
              <span className={styles.documentItemName}>{p.fileName}</span>
              <span className={styles.documentItemMeta}>
                Uploading {p.progress}%
              </span>
            </div>
          </div>
        ))}
    </>
  )

  return (
    <div>
      <div className={styles.documentSectionHeader}>
        <span className={styles.documentSectionLabel}>Evidence documents</span>
        <Tooltip title="Upload evidence documents (PHI/PII-free). Max 3 files per override.">
          <InfoCircleOutlined className={styles.documentSectionInfoIcon} />
        </Tooltip>
      </div>

      <div className={styles.documentSectionGrid}>
        <div className={styles.documentSectionUploadCol}>
          {readOnly ? (
            <DocumentDropzone
              onFilesSelected={() => {}}
              disabled
              fileCount={totalCount}
              maxFiles={MAX_FILES}
            />
          ) : totalCount < MAX_FILES ? (
            <DocumentDropzone
              onFilesSelected={handleFilesSelected}
              disabled={!isStaged && isDeleting}
              fileCount={totalCount}
              maxFiles={MAX_FILES}
            />
          ) : (
            <div className={styles.documentSectionLimitBox}>
              <p className={styles.documentSectionLimitNote}>
                Maximum {MAX_FILES} files reached. Remove a file to upload
                another.
              </p>
            </div>
          )}
        </div>
        <div className={styles.documentSectionFilesCol}>
          {totalCount === 0 ? (
            <div className={styles.documentSectionFilesEmpty}>
              <FileOutlined className={styles.documentSectionFilesEmptyIcon} />
              <p className={styles.documentSectionFilesEmptyText}>
                Uploaded files will appear here
              </p>
            </div>
          ) : (
            allFileItems
          )}
        </div>
      </div>
    </div>
  )
}
